import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { switchOrganization } from 'features/auth/services/auth-service';
import { reloadAfterOrgSwitch } from 'features/auth/utils/reload-after-org-switch';
import { useGetOrganizationQuery, useOrganizations } from 'features/organization/hooks/use-organization';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useTheme } from 'components/core/theme-provider';
import { HttpError } from 'lib/https';
import { cn } from 'lib/utils';

export const OrgSwitcher = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { selectedOrgId, setAccessToken, setRefreshToken, setSelectedOrgId } = useAuthStore();
  const user = useCommonStore((state) => state.user);
  const activeOrganizationId = selectedOrgId || user?.organizationId || '';
  const { data: organizationData } = useGetOrganizationQuery(activeOrganizationId);
  const { data, isLoading } = useOrganizations({
    page: 0,
    pageSize: 50,
    enabled: Boolean(configurationSettings.blocksKey),
  });

  const organizations = useMemo(
    () => (data?.data ?? []).filter((organization) => organization.isActive !== false),
    [data?.data]
  );
  const menuOrganizations = organizations.filter((organization) => {
    const name = organization.name?.toLowerCase();
    const id = organization.itemId?.toLowerCase();
    return name !== 'default' && id !== 'default';
  });

  const resolvedSelectedOrgId = activeOrganizationId || organizations[0]?.itemId || null;
  const selectedOrg =
    organizations.find((organization) => organization.itemId === resolvedSelectedOrgId) ??
    organizations.find((organization) => organization.name === organizationData?.name) ??
    (organizationData?.itemId === resolvedSelectedOrgId ? organizationData : undefined);

  if (isLoading || organizations.length <= 1) {
    return null;
  }

  const handleOrgSelect = async (orgId: string) => {
    if (isSwitching || orgId === resolvedSelectedOrgId) {
      return;
    }

    try {
      setIsSwitching(true);
      setIsDropdownOpen(false);

      const response = await switchOrganization(orgId, configurationSettings);

      setAccessToken(response.access_token);
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }
      setSelectedOrgId(orgId);
      reloadAfterOrgSwitch();
    } catch (error) {
      const errorTitle = t('FAILED_TO_SWITCH_ORGANIZATION');
      let errorDescription = t('SOMETHING_WENT_WRONG_SWITCHING_ORGANIZATIONS');

      if (error instanceof HttpError) {
        const errorData = error.error;

        if (typeof errorData?.error_description === 'string') {
          errorDescription = errorData.error_description;
        } else if (typeof errorData?.error === 'string') {
          errorDescription = errorData.error;
        }
      }

      toast({
        variant: 'destructive',
        title: errorTitle,
        description: errorDescription,
      });
      setIsSwitching(false);
    }
  };

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <button
          type="button"
          data-testid="org-switcher"
          disabled={isSwitching}
          aria-busy={isSwitching}
          className={cn(
            'flex h-8 items-center justify-center gap-1 rounded-full border px-3',
            theme === 'dark' ? 'border-border bg-[#0000]' : 'border-[#d1d1d1] bg-white'
          )}
        >
          <span className="max-w-[140px] truncate text-sm font-normal leading-[1.2] text-black dark:text-true-white">
            {selectedOrg?.name ?? t('ORGANIZATION')}
          </span>
          {isSwitching ? (
            <Loader2
              className="size-4 shrink-0 animate-spin text-black dark:text-true-white"
              data-testid="org-switcher-loader"
              aria-hidden
            />
          ) : (
            <ChevronDown
              className="size-4 shrink-0 text-black dark:text-true-white"
              aria-hidden
            />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[220px] rounded-2xl">
        {menuOrganizations.map((organization, index) => {
          const isSelected =
            organization.itemId === selectedOrg?.itemId ||
            organization.name === selectedOrg?.name;

          return (
            <div key={organization.itemId}>
              <DropdownMenuItem
                className={cn('pr-2', isSelected && 'font-bold')}
                onClick={() => handleOrgSelect(organization.itemId)}
              >
                {organization.name}
                <Check
                  className={cn(
                    'ml-auto size-4 shrink-0 text-current',
                    !isSelected && 'invisible'
                  )}
                  aria-hidden
                />
              </DropdownMenuItem>
              {index !== menuOrganizations.length - 1 && <DropdownMenuSeparator />}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
